import {
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  query,
  where,
} from "firebase/firestore";
import {
  Plus,
  Search,
  Users,
  UsersRound,
  Briefcase,
  Crown,
  Eye,
  Scale,
  X,
  Settings2,
  Trash2,
  Loader2,
} from "lucide-react";
import { ACTION_BTN_GOLD, ACTION_BTN_ICON } from "../../styles/actionButtons";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { TeamAvatars } from "../../components/crm/cases/CasePresentation";
import { getTeamIcon } from "../../components/crm/teams/teamTheme";
import { useAuth } from "../../context/AuthContext";
import { db } from "../../firebase";
import featureGatingService from "../../services/featureGatingService";

function TeamOverviewPage() {
  const { globalUser } = useAuth();
  const navigate = useNavigate();

  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [memberNames, setMemberNames] = useState({});
  const [myCases, setMyCases] = useState([]);
  const [planName, setPlanName] = useState("");
  const [search, setSearch] = useState("");

  // View-cases dialog state
  const [casesTeam, setCasesTeam] = useState(null);
  // Delete-team confirm state
  const [deleteTeam, setDeleteTeam] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const handleDeleteTeam = async () => {
    if (!deleteTeam) return;
    setDeleting(true);
    try {
      await deleteDoc(doc(db, "teams", deleteTeam.id));
      setDeleteTeam(null);
    } catch (e) {
      console.error("Error deleting team:", e);
    } finally {
      setDeleting(false);
    }
  };

  // ── Owned + org teams (live) ───────────────────────────────────────────────
  useEffect(() => {
    if (!globalUser?.uid) {
      setTeams([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);

    const ownedQ = query(
      collection(db, "teams"),
      where("ownerId", "==", globalUser.uid)
    );
    const memberQ = query(
      collection(db, "teams"),
      where("members", "array-contains", globalUser.uid)
    );

    let owned = [];
    let org = [];
    const merge = () => {
      const byId = new Map();
      [...owned, ...org].forEach((t) => byId.set(t.id, t));
      const list = [...byId.values()];
      // Default "shared to" team first, then by name.
      list.sort((a, b) => {
        if (a.isDefault && !b.isDefault) return -1;
        if (!a.isDefault && b.isDefault) return 1;
        return (a.teamName || "").localeCompare(b.teamName || "");
      });
      setTeams(list);
      setLoading(false);
    };

    const unsubOwned = onSnapshot(
      ownedQ,
      (snap) => {
        owned = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        merge();
      },
      (err) => {
        console.error("Error fetching teams:", err);
        setError("Failed to load teams. Please try again.");
        setLoading(false);
      }
    );
    const unsubOrg = onSnapshot(
      memberQ,
      (snap) => {
        org = snap.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .filter((t) => t.isOrgTeam);
        merge();
      },
      (err) => console.error("Error fetching org teams:", err)
    );

    return () => {
      unsubOwned();
      unsubOrg();
    };
  }, [globalUser]);

  // ── Plan name (for the "Current Plan" stat card) ───────────────────────────
  useEffect(() => {
    if (!globalUser?.uid) return;
    featureGatingService
      .canAddTeamMember(globalUser.uid)
      .then((info) => setPlanName(info?.planName || ""))
      .catch((e) => console.error("plan info:", e));
  }, [globalUser]);

  // ── Resolve every member uid → display name (for avatars) ──────────────────
  useEffect(() => {
    const uids = new Set();
    teams.forEach((t) => (t.members || []).forEach((u) => u && uids.add(u)));
    const missing = [...uids].filter((u) => !(u in memberNames));
    if (missing.length === 0) return;
    let cancelled = false;
    (async () => {
      const entries = await Promise.all(
        missing.map(async (uid) => {
          try {
            let snap = await getDoc(doc(db, "lawyers", uid));
            if (!snap.exists()) snap = await getDoc(doc(db, "client", uid));
            const d = snap.exists() ? snap.data() : {};
            return [uid, d.name || d.displayName || ""];
          } catch {
            return [uid, ""];
          }
        })
      );
      if (!cancelled)
        setMemberNames((prev) => ({ ...prev, ...Object.fromEntries(entries) }));
    })();
    return () => {
      cancelled = true;
    };
  }, [teams]); // eslint-disable-line

  // ── Fetch the user's cases once (for per-team + total shared-case counts) ──
  useEffect(() => {
    if (!globalUser?.uid) return;
    let cancelled = false;
    (async () => {
      try {
        const all = [];
        for (const col of ["pending", "disposed"]) {
          const snap = await getDocs(
            query(collection(db, col), where("owner", "==", globalUser.uid))
          );
          snap.docs.forEach((d) => {
            const c = d.data();
            all.push({
              id: d.id,
              caseNo: c.caseNo,
              petitionerName: c.petitionerName,
              respondentName: c.respondentName,
              courtName: c.courtName,
              status: c.status,
              team: Array.isArray(c.team) ? c.team : [],
            });
          });
        }
        if (!cancelled) setMyCases(all);
      } catch (e) {
        console.error("Error fetching cases for team stats:", e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [globalUser]);

  // Cases shared to a team = my cases whose team array includes every member.
  const casesForTeam = (team) => {
    const mem = team.members || [];
    if (!mem.length) return [];
    return myCases.filter((c) => mem.every((m) => c.team.includes(m)));
  };

  const stats = useMemo(() => {
    const memberSet = new Set();
    teams.forEach((t) => (t.members || []).forEach((m) => m && memberSet.add(m)));
    const sharedCases = myCases.filter((c) => (c.team || []).length > 1).length;
    return {
      totalTeams: teams.length,
      totalMembers: memberSet.size,
      sharedCases,
    };
  }, [teams, myCases]);

  const filteredTeams = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return teams;
    return teams.filter((t) => (t.teamName || "").toLowerCase().includes(q));
  }, [teams, search]);

  const STAT_CARDS = [
    { icon: Users, label: "Total Teams", value: stats.totalTeams, tint: "bg-blue-50 text-blue-600" },
    { icon: UsersRound, label: "Total Members", value: stats.totalMembers, tint: "bg-amber-50 text-amber-600" },
    { icon: Briefcase, label: "Shared Cases", value: stats.sharedCases, tint: "bg-emerald-50 text-emerald-600" },
    { icon: Crown, label: "Current Plan", value: planName || "—", tint: "bg-violet-50 text-violet-600" },
  ];

  return (
    <div className="min-h-screen bg-[#F8FAFC] text-gray-900">
      <main className="px-4 sm:px-6 lg:px-8 py-4">
        {/* Header */}
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <h1 className="text-xl font-bold tracking-tight text-slate-900 sm:text-2xl">
              Teams Overview
            </h1>
            <p className="mt-0.5 text-xs text-slate-500">
              Manage your teams and collaborate effectively
            </p>
          </div>
          <button
            type="button"
            onClick={() => navigate("/portal/manage-teams")}
            className={ACTION_BTN_GOLD}
          >
            <Plus className={ACTION_BTN_ICON} /> Create Team
          </button>
        </div>

        {/* Stat cards */}
        <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {STAT_CARDS.map((s) => {
            const Icon = s.icon;
            return (
              <div
                key={s.label}
                className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm"
              >
                <span className={`flex h-11 w-11 items-center justify-center rounded-xl ${s.tint}`}>
                  <Icon className="h-5 w-5" />
                </span>
                <div className="min-w-0">
                  <p className="truncate text-xl font-bold text-slate-900">{s.value}</p>
                  <p className="truncate text-xs text-slate-500">{s.label}</p>
                </div>
              </div>
            );
          })}
        </div>

        {/* Search */}
        <div className="relative mt-5">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search teams..."
            className="w-full rounded-xl border border-slate-200 bg-white py-2.5 pl-10 pr-4 text-sm text-slate-900 shadow-sm placeholder:text-slate-400 focus:border-yellow-400 focus:outline-none focus:ring-2 focus:ring-yellow-400/40"
          />
        </div>

        {/* Loading */}
        {loading && (
          <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="h-48 animate-pulse rounded-2xl border border-slate-200 bg-white" />
            ))}
          </div>
        )}

        {/* Error */}
        {!loading && error && (
          <div className="mt-6 rounded-2xl border border-red-200 bg-red-50 p-4 text-center text-red-600">
            {error}
          </div>
        )}

        {/* Empty */}
        {!loading && !error && teams.length === 0 && (
          <div className="mt-6 flex flex-col items-center justify-center rounded-2xl border border-slate-200 bg-white py-16 text-center shadow-sm">
            <Users className="mb-3 h-12 w-12 text-slate-300" />
            <p className="text-lg font-medium text-slate-700">No teams yet</p>
            <p className="mt-1 text-sm text-slate-500">
              Create your first team to start collaborating.
            </p>
            <button
              type="button"
              onClick={() => navigate("/portal/manage-teams")}
              className={`mt-4 ${ACTION_BTN_GOLD}`}
            >
              <Plus className={ACTION_BTN_ICON} /> Create Team
            </button>
          </div>
        )}

        {/* Team cards */}
        {!loading && !error && teams.length > 0 && (
          <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {filteredTeams.map((team) => {
              const count = (team.members || []).length;
              const members = (team.members || []).map((uid) => ({
                uid,
                name: memberNames[uid] || "",
              }));
              const owned = team.ownerId === globalUser?.uid;
              const caseCount = casesForTeam(team).length;
              const TeamIcon = getTeamIcon(team.teamIcon);
              const accent = team.teamColor || "#64748B";
              return (
                <div
                  key={team.id}
                  className="flex flex-col rounded-2xl border border-slate-200 bg-white p-5 shadow-sm transition-shadow hover:shadow-md"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex min-w-0 items-start gap-3">
                      <span
                        className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl"
                        style={{ backgroundColor: `${accent}1A`, color: accent }}
                      >
                        {TeamIcon ? (
                          <TeamIcon className="h-5 w-5" />
                        ) : (
                          <span className="text-sm font-bold">
                            {(team.teamName || "?").charAt(0).toUpperCase()}
                          </span>
                        )}
                      </span>
                      <div className="min-w-0">
                        <h3 className="truncate font-bold text-slate-900" title={team.teamName}>
                          {team.teamName || "Unnamed Team"}
                        </h3>
                        <p className="mt-0.5 text-xs text-slate-400">
                          {count} {count === 1 ? "Member" : "Members"}
                        </p>
                      </div>
                    </div>
                    {owned && !team.isDefault && (
                      <div className="flex shrink-0 items-center gap-0.5">
                        <button
                          type="button"
                          onClick={() => navigate(`/portal/manage-teams/${team.id}`)}
                          title="Manage members"
                          className="rounded-lg p-1.5 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700"
                        >
                          <Settings2 className="h-4 w-4" />
                        </button>
                        <button
                          type="button"
                          onClick={() => setDeleteTeam(team)}
                          title="Delete team"
                          className="rounded-lg p-1.5 text-slate-400 transition-colors hover:bg-red-50 hover:text-red-600"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    )}
                  </div>

                  <div className="mt-4">
                    {count > 0 ? (
                      <TeamAvatars members={members} max={3} />
                    ) : (
                      <span className="text-sm text-slate-400">No members yet</span>
                    )}
                  </div>

                  <p className="mt-4 text-sm font-medium text-slate-600">
                    {caseCount} {caseCount === 1 ? "Case" : "Cases"}
                  </p>

                  <button
                    type="button"
                    onClick={() => setCasesTeam(team)}
                    className="mt-3 inline-flex w-full items-center justify-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700 shadow-sm transition-colors hover:border-slate-300 hover:bg-slate-50"
                  >
                    <Eye className="h-4 w-4" /> View Cases
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </main>

      {/* View-cases dialog */}
      {casesTeam && (
        <div
          className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 p-4 pt-16"
          onClick={() => setCasesTeam(null)}
        >
          <div
            className="w-full max-w-3xl rounded-2xl border border-slate-200 bg-white shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between border-b border-slate-100 px-5 py-4">
              <h2 className="text-base font-bold text-slate-900">
                {casesTeam.teamName} — Cases
              </h2>
              <button
                type="button"
                onClick={() => setCasesTeam(null)}
                className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="max-h-[65vh] overflow-y-auto p-5">
              {casesForTeam(casesTeam).length === 0 ? (
                <p className="py-10 text-center text-sm text-slate-500">
                  No cases shared with this team.
                </p>
              ) : (
                <div className="space-y-2">
                  {casesForTeam(casesTeam).map((c) => {
                    const disposed = (c.status || "").toLowerCase() === "disposed";
                    return (
                      <button
                        key={c.id}
                        type="button"
                        onClick={() => {
                          setCasesTeam(null);
                          navigate(`/portal/case/${c.id}`);
                        }}
                        className="flex w-full items-center gap-3 rounded-xl border border-slate-200 bg-white p-3 text-left transition-colors hover:bg-slate-50"
                      >
                        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-slate-100">
                          <Scale className="h-5 w-5 text-slate-500" />
                        </span>
                        <div className="min-w-0 flex-grow">
                          <p className="truncate font-semibold text-slate-800">
                            {[c.petitionerName, c.respondentName].filter(Boolean).join(" vs ") ||
                              "Untitled case"}
                          </p>
                          <p className="truncate text-xs text-slate-500">
                            {c.caseNo || "—"} · {c.courtName || "—"}
                          </p>
                        </div>
                        <span
                          className={`shrink-0 rounded-full px-2.5 py-0.5 text-xs font-medium ring-1 ring-inset ${
                            disposed
                              ? "bg-violet-50 text-violet-700 ring-violet-200"
                              : "bg-amber-50 text-amber-700 ring-amber-200"
                          }`}
                        >
                          {c.status
                            ? c.status.charAt(0).toUpperCase() + c.status.slice(1).toLowerCase()
                            : "—"}
                        </span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Delete-team confirm */}
      {deleteTeam && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
          onClick={() => !deleting && setDeleteTeam(null)}
        >
          <div
            className="w-full max-w-sm rounded-2xl border border-slate-200 bg-white p-6 shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start gap-3">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-red-50 text-red-600">
                <Trash2 className="h-5 w-5" />
              </span>
              <div className="min-w-0">
                <h2 className="text-base font-bold text-slate-900">Delete team</h2>
                <p className="mt-1 text-sm text-slate-500">
                  Delete <span className="font-semibold text-slate-700">{deleteTeam.teamName}</span>?
                  This removes the team for everyone and can't be undone. Shared cases are not affected.
                </p>
              </div>
            </div>
            <div className="mt-5 flex items-center justify-end gap-2">
              <button
                type="button"
                disabled={deleting}
                onClick={() => setDeleteTeam(null)}
                className="inline-flex items-center rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 shadow-sm transition-colors hover:bg-slate-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={deleting}
                onClick={handleDeleteTeam}
                className="inline-flex items-center gap-2 rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-red-700 disabled:opacity-50"
              >
                {deleting ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default TeamOverviewPage;
